import { useState, useRef, useEffect, useCallback } from "react"
import { MessageCircle, X, Send, Sparkles } from "lucide-react"
import ReactMarkdown from "react-markdown"
import type { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/use-auth"
import { streamChatMessage, type ChatHistoryItem } from "@/services/chat-sse"

interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: number
  toolStatus?: { name: string; label: string } | null
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function describeTool(name: string): string {
  if (name === "search_faq") return "la FAQ Qualneo"
  if (name.startsWith("qualneo_")) return "tes données"
  return name
}

// Liens gardés : web, courriel, chemin de l'application.
// Aucun autre schéma, ni javascript: ni data:.
const SAFE_LINK = /^(https?:|mailto:|\/(?!\/)|#)/i

function chatUrlTransform(url: string): string {
  return SAFE_LINK.test(url.trim()) ? url : ""
}

const CHAT_REMARK_PLUGINS = [remarkGfm]

// Lien neutralisé : du texte, ni cliquable ni stylé en lien.
// Image : jamais chargée, son URL partirait vers un tiers.
// Seul son texte alternatif reste.
const CHAT_MARKDOWN_COMPONENTS: Components = {
  a: ({ node: _node, href, children, ...props }) =>
    href ? (
      <a href={href} {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  img: ({ alt }) => (alt ? <span>{alt}</span> : null),
}

// Réponse du modèle rendue en Markdown, sans HTML brut.
export function ChatMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={CHAT_REMARK_PLUGINS}
      skipHtml={true}
      urlTransform={chatUrlTransform}
      components={CHAT_MARKDOWN_COMPONENTS}
    >
      {content}
    </ReactMarkdown>
  )
}

export function AIChatWidget() {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Le chat IA est gaté Équipe+ côté backend (cohérent avec /tarifs sur le
  // site marketing). Le widget ne se monte donc que pour ces plans — pas
  // d'upsell intrusif depuis la barre de chat.
  const hasChatAccess =
    user !== null && (user.plan === "equipe" || user.plan === "enterprise")

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Cancel any in-flight stream if the widget closes / unmounts.
  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  useEffect(() => {
    if (!isOpen) abortRef.current?.abort()
  }, [isOpen])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      if (e.key === "Escape") {
        setIsOpen(false)
        return
      }

      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [isOpen],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  function updateAssistant(id: string, mutator: (msg: ChatMessage) => ChatMessage): void {
    setMessages((prev) => prev.map((m) => (m.id === id ? mutator(m) : m)))
  }

  function handleSend() {
    const trimmed = input.trim()
    if (!trimmed || isStreaming) return

    const userMessage: ChatMessage = {
      id: generateId(),
      role: "user",
      content: trimmed,
      timestamp: Date.now(),
    }
    const assistantId = generateId()
    const assistantPlaceholder: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
      toolStatus: null,
    }

    // Replay the prior turns so the assistant keeps context across messages.
    // `messages` here is the thread *before* this send (the new user turn and
    // placeholder are appended below), and empty placeholders are filtered out.
    const history: ChatHistoryItem[] = messages
      .filter((m) => m.content.trim().length > 0)
      .map((m) => ({ role: m.role, content: m.content }))

    setMessages((prev) => [...prev, userMessage, assistantPlaceholder])
    setInput("")
    setIsStreaming(true)

    const abort = new AbortController()
    abortRef.current = abort

    streamChatMessage(
      trimmed,
      {
        onChunk: (delta) => {
          updateAssistant(assistantId, (m) => ({ ...m, content: m.content + delta }))
        },
        onToolCallStart: (name) => {
          updateAssistant(assistantId, (m) => ({
            ...m,
            toolStatus: { name, label: `Je consulte ${describeTool(name)}…` },
          }))
        },
        onToolCallResult: () => {
          updateAssistant(assistantId, (m) => ({ ...m, toolStatus: null }))
        },
        onDone: () => {
          updateAssistant(assistantId, (m) => ({ ...m, toolStatus: null }))
          setIsStreaming(false)
        },
        onError: (_code, msg) => {
          updateAssistant(assistantId, (m) => ({
            ...m,
            content:
              m.content ||
              `Désolé, je n'ai pas pu répondre : ${msg}. Réessaie dans quelques secondes.`,
            toolStatus: null,
          }))
          setIsStreaming(false)
        },
      },
      abort.signal,
      history,
    ).catch(() => {
      // Already routed through onError; abort cancellations are silent.
      setIsStreaming(false)
    })
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!hasChatAccess) return null

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Ouvrir l'assistant IA"
          data-test="chat-trigger"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Assistant IA Qualneo"
          aria-modal="true"
          className="fixed bottom-6 right-6 z-50 flex h-[32rem] w-96 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
          data-test="chat-panel"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Assistant Qualneo</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsOpen(false)}
              aria-label="Fermer l'assistant IA"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p
            className="border-b border-border px-4 py-2 text-xs text-muted-foreground"
            data-test="chat-ai-disclosure"
          >
            Tu discutes avec une IA — vérifie les informations importantes.
          </p>

          <div
            className="flex-1 overflow-y-auto px-4 py-3"
            aria-live="polite"
            aria-atomic="false"
            data-test="chat-messages"
          >
            {messages.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
                <MessageCircle className="mb-3 h-8 w-8 opacity-40" />
                <p className="font-medium">Une question ?</p>
                <p className="mt-1 text-xs">
                  Je peux consulter tes données et la doc Qualiopi pour répondre précisément.
                </p>
                <p className="mt-3 text-xs opacity-70">Réponses générées par IA.</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "mb-3 max-w-[85%] rounded-xl px-3 py-2 text-sm",
                  msg.role === "user"
                    ? "ml-auto bg-primary text-primary-foreground"
                    : "mr-auto bg-muted text-foreground",
                )}
                data-test={`chat-message-${msg.role}`}
              >
                {msg.content && (
                  <div
                    className={cn(
                      "prose prose-sm max-w-none break-words",
                      "prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1",
                      "prose-pre:my-2 prose-pre:bg-background prose-pre:text-foreground",
                      "prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:bg-background",
                      msg.role === "user"
                        ? "prose-invert"
                        : "prose-neutral dark:prose-invert",
                    )}
                    data-test={`chat-message-content-${msg.role}`}
                  >
                    <ChatMarkdown content={msg.content} />
                  </div>
                )}
                {msg.toolStatus && (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs italic text-muted-foreground">
                    <Sparkles className="h-3 w-3 animate-pulse" />
                    {msg.toolStatus.label}
                  </p>
                )}
                {!msg.content && !msg.toolStatus && isStreaming && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <span className="animate-pulse">...</span>
                  </span>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-border px-3 py-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Pose ta question..."
                className="max-h-24 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                rows={1}
                disabled={isStreaming}
                aria-label="Message pour l'assistant IA"
                data-test="chat-input"
              />
              <Button
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={handleSend}
                disabled={isStreaming || !input.trim()}
                aria-label="Envoyer le message"
                data-test="chat-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
